import { useEffect, useState } from "react";
import axios from "axios";
import { useGeo } from "@/context/GeoContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Phase 8M.3 Item 2 — Dynamic "DEALS NEAR YOU" rail.
 *
 * Pulls 4 LLM-generated deals (mix of 2 flights + 2 holiday packages)
 * tailored to the user's country + state + current month from
 * /api/deals. Refetches whenever the resolved geo signal updates
 * (GPS unlock, manual currency switch, IP fallback completes).
 *
 * Falls back to the curated static deals included server-side when the
 * LLM call fails so the rail never collapses to an empty box.
 */
export default function DealsRail({ onPick }) {
    const { geo, userGeo } = useGeo();
    const [deals, setDeals] = useState([]);
    const [loading, setLoading] = useState(true);

    const country = (userGeo?.country_code || geo?.country_code || "DEFAULT").toUpperCase();
    const state = (userGeo?.region || geo?.region || geo?.region_code || "").toString().slice(0, 6).toUpperCase();
    const month = new Date().getMonth() + 1;

    useEffect(() => {
        if (!geo && !userGeo) return;
        let cancelled = false;
        async function load() {
            setLoading(true);
            try {
                const params = { country, month };
                if (state) params.state = state;
                const res = await axios.get(`${API}/deals`, { params, timeout: 12000 });
                if (!cancelled) setDeals((res.data?.items || []).slice(0, 4));
            } catch (e) {
                if (!cancelled) setDeals([]);
                console.warn("[deals] failed", e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        load();
        return () => {
            cancelled = true;
        };
    }, [country, state, month, geo, userGeo]);

    if (loading || deals.length === 0) return null;

    return (
        <aside
            className="hidden lg:flex lg:flex-col lg:gap-3"
            data-testid="deals-rail"
        >
            <h3 className="px-1 text-[11px] font-semibold uppercase tracking-wider text-[#737373]">
                Deals near you
            </h3>
            <div className="flex flex-col gap-3">
                {deals.map((d, i) => {
                    const dot = d.badge_color || (d.type === "flight" ? "#2563EB" : "#7C3AED");
                    const priceLabel = d.price != null
                        ? `${d.currency} ${Number(d.price).toLocaleString()}`
                        : null;
                    return (
                        <button
                            key={d.id || `${d.type}-${i}`}
                            type="button"
                            onClick={() => onPick(d.deeplink || d.title)}
                            data-testid={`deal-square-${i}`}
                            className="group relative flex h-[110px] w-[180px] flex-col justify-between rounded-[12px] border border-[#E5E5E5] bg-white p-3 text-left transition hover:border-[#0A0A0A]"
                        >
                            <span
                                aria-hidden
                                className="absolute right-2.5 top-2.5 inline-block h-2 w-2 rounded-full"
                                style={{ backgroundColor: dot, boxShadow: `0 0 0 4px ${dot}1A` }}
                                data-testid={`deal-badge-${i}`}
                            />
                            <div>
                                <div
                                    className="line-clamp-2 text-[12.5px] font-semibold leading-tight tracking-tight text-[#0A0A0A]"
                                    data-testid={`deal-title-${i}`}
                                >
                                    {d.title}
                                </div>
                                {d.destination && (
                                    <div className="mt-1 text-[10.5px] text-[#737373]">
                                        {d.destination}
                                    </div>
                                )}
                            </div>
                            <div className="flex items-end justify-between">
                                <span className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">
                                    {d.type === "flight" ? "Flight" : "Package"}
                                </span>
                                {priceLabel && (
                                    <span
                                        className="text-[13px] font-bold tracking-tight text-[#0A0A0A]"
                                        data-testid={`deal-price-${i}`}
                                    >
                                        {priceLabel}
                                    </span>
                                )}
                            </div>
                        </button>
                    );
                })}
            </div>
        </aside>
    );
}
